import {CommonModule} from '@angular/common';
import {Component, DestroyRef, inject, OnInit, viewChild, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {SeFeCardModule} from 'se-fe-card';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFePaginationOptions} from 'se-fe-pagination';
import {CatalogsQueryParams, CatalogSummary, DEFAULT_CATALOGS_QUERY_PARAMS} from '../../models/clare';
import {ListPageScrollService} from '../../utils/pagination-utils';
import {CatalogTableComponent} from './catalog-table/catalog-table.component';
import {CreateCatalogModalComponent} from './create-catalog/create-catalog-modal.component';
import {DeleteCatalogModalComponent} from './delete-catalog/delete-catalog-modal.component';
import {CatalogActions} from './store/catalog.actions';
import {selectCatalogPaginationOptions, selectCatalogs, selectCatalogsLoading} from './store/catalog.selectors';

@Component({
  selector: 'app-catalog-list',
  templateUrl: './catalog-list.component.html',
  styleUrl: '../commerce-list.scss',
  host: {class: 'app-catalog-list'},
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CatalogTableComponent,
    CreateCatalogModalComponent,
    DeleteCatalogModalComponent,
    CommonModule,
    SeFeCardModule,
    SeFeGridModule,
  ],
})
export class CatalogListComponent implements OnInit {
  readonly createCatalogModal = viewChild.required<CreateCatalogModalComponent>('createCatalogModal');
  readonly deleteCatalogModal = viewChild.required<DeleteCatalogModalComponent>('deleteCatalogModal');

  private destroyRef = inject(DestroyRef);

  catalogs$: Observable<CatalogSummary[]>;
  loading$: Observable<boolean>;
  paginationOptions$: Observable<SeFePaginationOptions>;

  queryParams: CatalogsQueryParams = {...DEFAULT_CATALOGS_QUERY_PARAMS};

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private listPageScroll: ListPageScrollService
  ) {
    this.catalogs$ = this.store.select(selectCatalogs);
    this.loading$ = this.store.select(selectCatalogsLoading);
    this.paginationOptions$ = this.store.select(selectCatalogPaginationOptions);
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const queryParams = this.buildQueryParams(params);

      if (this.missingDefaultQueryParams(params)) {
        this.navigateToQueryParams(queryParams, true);
        return;
      }

      this.loadCatalogs(queryParams);
    });
  }

  queryChanged(queryParams: Partial<CatalogsQueryParams>): void {
    const mergedParams = this.mergeQueryParams(queryParams);
    this.navigateToQueryParams(mergedParams);
    this.listPageScroll.scrollToTop();
  }

  openCreateCatalog(trigger: HTMLElement): void {
    this.createCatalogModal().open(trigger);
  }

  openDeleteCatalog(catalog: CatalogSummary, trigger: HTMLElement): void {
    this.deleteCatalogModal().open(catalog, trigger);
  }

  private loadCatalogs(queryParams: CatalogsQueryParams): void {
    this.queryParams = queryParams;
    this.store.dispatch(CatalogActions.loadCatalogs({queryParams}));
  }

  private mergeQueryParams(incoming: Partial<CatalogsQueryParams>): CatalogsQueryParams {
    return {
      ...this.queryParams,
      ...incoming,
      page: incoming.page ?? this.queryParams.page ?? DEFAULT_CATALOGS_QUERY_PARAMS.page,
      per_page: incoming.per_page ?? this.queryParams.per_page ?? DEFAULT_CATALOGS_QUERY_PARAMS.per_page,
    };
  }

  private navigateToQueryParams(queryParams: CatalogsQueryParams, replaceUrl = false): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.buildUrlQueryParams(queryParams),
      replaceUrl,
    });
  }

  private buildQueryParams(params: Params): CatalogsQueryParams {
    const queryParams: CatalogsQueryParams = {
      page: this.toPositiveInteger(params['page'], DEFAULT_CATALOGS_QUERY_PARAMS.page),
      per_page: this.toPositiveInteger(params['per_page'], DEFAULT_CATALOGS_QUERY_PARAMS.per_page),
    };
    const search = this.toOptionalString(params['search']);
    const orderBy = this.toOptionalString(params['order_by']);
    const direction = this.toSortDirection(params['direction']);

    if (search !== undefined) queryParams.search = search;
    if (orderBy !== undefined) queryParams.order_by = orderBy;
    if (direction !== undefined) queryParams.direction = direction;

    return queryParams;
  }

  private buildUrlQueryParams(queryParams: CatalogsQueryParams): Params {
    return {
      page: queryParams.page,
      per_page: queryParams.per_page,
      search: queryParams.search || undefined,
      order_by: queryParams.order_by || undefined,
      direction: queryParams.direction || undefined,
    };
  }

  private missingDefaultQueryParams(params: Params): boolean {
    return params['page'] === undefined || params['per_page'] === undefined;
  }

  private toOptionalString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() ? value : undefined;
  }

  private toPositiveInteger(value: unknown, fallback: number): number {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? Math.floor(parsedValue) : fallback;
  }

  private toSortDirection(value: unknown): CatalogsQueryParams['direction'] | undefined {
    return value === 'asc' || value === 'desc' ? value : undefined;
  }
}
